import { useEffect, useState } from "react";
import withdrawService from "../services/withdraw.service";
import { BottomDrawer } from "../../../components/ui/BottomDrawer";
import localization from "../../../config/localization";
import {
  UserPendingWithdrawalResponse,
  WithdrawStatus,
  WithdrawTypeListResponseList,
  WithdrawVerificationStatusResponse,
} from "../models";
import { Button } from "../../../components/ui";
import { useNavigate } from "react-router-dom";
import { ConfirmationModal } from "../../../components/ui/modals/ConfirmationModal";
import { DepositFlow } from "../../deposit/models/constants/depositFlow";
import { useAppDispatch } from "../../../store/hooks";
import { changeLoaderState } from "../../../reducers/appStateReducer";
import WithdrawV1 from "./WithdrawV1";
import WithdrawV2 from "./WithdrawV2";
import { toastService } from "../../../components/ui/toast/toast.service";
import { Modal } from "../../../components/ui/modals";

export function Withdraw() {
  const [showWithdrawVerificationModal, setShowWithdrawVerificationModal] =
    useState<boolean>(false);
  const [withdrawVerificationStatus, setWithdrawVerificationStatus] =
    useState<WithdrawVerificationStatusResponse | null>(null);
  const [showProcessingModal, setShowProcessingModal] =
    useState<boolean>(false);
  const [userPendingWithdrawal, setUserPendingWithdrawal] =
    useState<UserPendingWithdrawalResponse | null>(null);
  const [showRevertConfirmationModal, setShowRevertConfirmationModal] =
    useState<boolean>(false);
  const [withdrawTypes, setWithdrawTypes] = useState<
    WithdrawTypeListResponseList[] | null
  >(null);
  const [depositFlow, setDepositFlow] = useState<string | null>(null);
  const [withdrawFlow, setWithdrawFlow] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(changeLoaderState(true));
    init();
  }, []);

  const init = async () => {
    const response = await withdrawService.getWithdrawVerificationStatus();
    if (response) {
      setWithdrawVerificationStatus(response);
      if (!response.iswithdrawallowed) {
        setShowWithdrawVerificationModal(true);
        dispatch(changeLoaderState(false));
        return;
      }
      if (response.iswithdrawprocessing) {
        const response = await withdrawService.getUserPendingWithdrawals();
        if (response) {
          setUserPendingWithdrawal(response);
          setShowProcessingModal(true);
        }
        dispatch(changeLoaderState(false));
        return;
      }
      const withdrawFlowResponse =
        await withdrawService.getWithdrawFlowDetails();
      if (withdrawFlowResponse) {
        setWithdrawFlow(withdrawFlowResponse.details[0].value);
        const userWithdrawSummary =
          await withdrawService.getUserWithdrawSummary();
        if (userWithdrawSummary) {
          const withdrawTypes = await withdrawService.getWithdrawTypes(
            withdrawFlowResponse.details[0].value,
            userWithdrawSummary,
          );
          if (withdrawTypes) {
            setWithdrawTypes(withdrawTypes);
            const depositFlowResponse =
              await withdrawService.getDepositFlowDetails();
            if (depositFlowResponse) {
              setDepositFlow(depositFlowResponse.value);
            }
          }
        }
      }
    }
    dispatch(changeLoaderState(false));
  };

  const handleProofClick = () => {
    navigate("/verification");
  };

  const handleBankClick = () => {
    navigate("/verification");
  };

  const handleMobileEmailClick = () => {
    navigate("/profile");
  };

  const handleBottomDrawerClose = () => {
    setShowWithdrawVerificationModal(false);

    if (
      !withdrawVerificationStatus?.isproofverified ||
      !withdrawVerificationStatus?.isbankverified
    ) {
      navigate("/verification");
    } else if (
      !withdrawVerificationStatus?.ismobileverified ||
      !withdrawVerificationStatus?.isemailverified
    ) {
      navigate("/profile");
    }
  };

  const handleProcessingModalClose = () => {
    setShowProcessingModal(false);
    navigate("/withdrawhistory");
  };

  const handleRevertWithdrawalClick = () => {
    setShowRevertConfirmationModal(true);
  };

  const handleRevertWithdrawalConfirmClick = async () => {
    if (userPendingWithdrawal) {
      const response = await withdrawService.revertUserWithdrawal(
        userPendingWithdrawal,
      );
      if (response) {
        setShowProcessingModal(false);
        setShowRevertConfirmationModal(false);
        await init();
        toastService.success(localization.withdraw_success_reverted);
      } else {
        navigate("/withdrawhistory");
      }
    }
  };

  return (
    <div>
      <BottomDrawer
        open={showWithdrawVerificationModal}
        onClose={handleBottomDrawerClose}
        className="w-full lg:max-w-[80%] lg:mx-auto"
      >
        <div className="p-4">
          <h2 className="text-center text-xl font-semibold">
            {localization.withdraw}
          </h2>
          <p className="mt-3 text-center text-sm font-medium text-[#A9A9A9]">
            {localization.withdraw_note}
          </p>
          <div className="mt-10 space-y-10 relative">
            <Button
              className="w-full relative rounded-md border border-[#353535] px-4 py-5 flex items-center justify-between"
              disabled={withdrawVerificationStatus?.isproofverified}
              onClick={handleProofClick}
            >
              <span className="text-sm font-medium">
                {localization.proof_approval}
              </span>
              <img
                src={
                  withdrawVerificationStatus?.isproofverified
                    ? "/icons/ic-correct.svg"
                    : "/icons/ic-warning.svg"
                }
                alt="status"
                className="w-8 h-8"
              />
              <span className="absolute left-1/2 -bottom-10 h-10 border-l border-dashed border-[#8D8D8D]" />
            </Button>

            <Button
              className="w-full relative rounded-md border border-[#353535] px-4 py-5 flex items-center justify-between"
              disabled={withdrawVerificationStatus?.isbankverified}
              onClick={handleBankClick}
            >
              <span className="text-sm font-medium">
                {localization.bank_doc_approval}
              </span>
              <img
                src={
                  withdrawVerificationStatus?.isbankverified
                    ? "/icons/ic-correct.svg"
                    : "/icons/ic-warning.svg"
                }
                alt="status"
                className="w-8 h-8"
              />
              <span className="absolute left-1/2 -bottom-10 h-10 border-l border-dashed border-[#8D8D8D]" />
            </Button>

            <Button
              className="w-full relative rounded-md border border-[#353535] px-4 py-5 flex items-center justify-between"
              disabled={
                withdrawVerificationStatus?.ismobileverified ||
                withdrawVerificationStatus?.isemailverified
              }
              onClick={handleMobileEmailClick}
            >
              <span className="text-sm font-medium">
                {localization.mobile_email_verification}
              </span>
              <img
                src={
                  withdrawVerificationStatus?.ismobileverified ||
                    withdrawVerificationStatus?.isemailverified
                    ? "/icons/ic-correct.svg"
                    : "/icons/ic-warning.svg"
                }
                alt="status"
                className="w-8 h-8"
              />
            </Button>
          </div>
        </div>
      </BottomDrawer>

      {showProcessingModal && (
        <Modal isOpen={showProcessingModal} showImg={false} onClose={handleProcessingModalClose}>
          <p className="mt-2 px-4 text-[18px] font-semibold leading-[28px] text-gray-600">
            {localization.withdrawProcessingDetails}
          </p>

          <div className="flex flex-row gap-2.5 w-full mt-6">
            <Button
              onClick={handleProcessingModalClose}
              className="flex-1 rounded-lg bg-[#EDEDED] py-3.5 font-semibold text-[#111] hover:opacity-90 transition-all duration-200"
            >
              {localization.ok_btn}
            </Button>

            {userPendingWithdrawal &&
              userPendingWithdrawal.isrevertallowed &&
              [
                WithdrawStatus.Pending,
                WithdrawStatus.Awaited,
                WithdrawStatus.Verified,
              ].some(
                (status) =>
                  status.toLowerCase() ===
                  userPendingWithdrawal.status.toLowerCase(),
              ) && (
                <Button
                  onClick={handleRevertWithdrawalClick}
                  className="flex-1 rounded-lg bg-black py-3.5 font-semibold  transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {localization.revert}
                </Button>
              )}
          </div>
        </Modal>
      )}

      {showRevertConfirmationModal && (
        <ConfirmationModal
          isOpen={showRevertConfirmationModal}
          onConfirm={() => {
            handleRevertWithdrawalConfirmClick();
          }}
          onCancel={() => {
            setShowRevertConfirmationModal(false);
          }}
          title={localization.withdraw_revert_confirmation_label}
          description={`\u20B9${Number(userPendingWithdrawal?.amount)?.toFixed(2)}`}
          descriptionClassName="text-center text-[34px] font-medium"
          icon={"/icons/ic-info.svg"}
          blurColor="bg-[#FFE6CC]"
          confirmText={localization.yes}
          cancelText={localization.no}
        />
      )}

      <div>
        {depositFlow &&
          withdrawFlow &&
          (depositFlow === DepositFlow.OldFlow ? (
            <WithdrawV1
              withdrawFlow={withdrawFlow}
              withdrawTypes={withdrawTypes ?? []}
              username={withdrawVerificationStatus?.username ?? null}
            />
          ) : (
            <WithdrawV2
              withdrawFlow={withdrawFlow}
              withdrawTypes={withdrawTypes ?? []}
              username={withdrawVerificationStatus?.username ?? null}
            />
          ))}
      </div>
    </div>
  );
}

export default Withdraw;
